
import React, { useEffect, useState } from "react";

import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { CHANGE_REQUEST_URL } from "../../../Constants/Constants";
import AxiosInstance from "../../../services/AxiosInstance";
import TaskDialog from "./../../task/TaskDialog";
import { useAlert } from "../../alerts/AlertContext";
import ChangeTaskDialog from "../ChangeTaskDialog";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseTaskDialog from "../CloseTaskDialog";

const TasksTab = ({ change }) => {

    const showAlert = useAlert();

    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [taskDialogOpen, setTaskDialogOpen] =
        useState(false);


    const [selectedTaskId, setSelectedTaskId] = useState(null);
    const [taskViewMode, setTaskViewMode] = useState(false);

    const [closeTaskDialogOpen, setCloseTaskDialogOpen] =
        useState(false);

    const [selectedTask, setSelectedTask] =
        useState(null);

    const handleAddTask = () => {
        setSelectedTaskId(null);
        setTaskViewMode(false);
        setTaskDialogOpen(true);
    };

    const onComplete = (taskId) => {
        setSelectedTaskId(taskId);
        setCloseTaskDialogOpen(true);
    };

    const handleTaskComplete = async (comments) => {
        if (!selectedTaskId) return;

        try {
            await AxiosInstance.put(
                `${CHANGE_REQUEST_URL}/${change.id}/tasks/${selectedTaskId}`,
                {
                    comments: comments
                }
            );

            setCloseTaskDialogOpen(false);
            setSelectedTaskId(null);

            await loadTasks();

            showAlert(
                "Task moved to completed state!",
                "success"
            );

        } catch (error) {
            console.error(error);

            showAlert(
                "Failed to move task to completed state!",
                "error"
            );
        }
    };

    // const onComplete = (taskId) => {

    //     setCloseTaskDialogOpen(true);

    //     AxiosInstance.put(`${CHANGE_REQUEST_URL}/${change.id}/tasks/${taskId}`)
    //         .then((response) => {
    //             loadTasks();
    //             showAlert("Moved task to completed state!", "success");
    //         })
    //         .catch((error) => {
    //             showAlert("Failed to Move task to completed state!", "error");
    //             console.error(error);
    //         });
    // };

    const handleViewTask = (taskId) => {
        console.log("View Task:", taskId);

        setSelectedTaskId(taskId);
        setTaskViewMode(true);
        setTaskDialogOpen(true);
    };

    const handleUpdateTask = (taskId) => {
        console.log("View Task:", taskId);

        setSelectedTaskId(taskId);
        setTaskViewMode(false);
        setTaskDialogOpen(true);
    };

    const handleTaskSaved = async () => {
        setTaskDialogOpen(false);
        setSelectedTaskId(null);
        setTaskViewMode(false);

        await loadTasks();
    };

    const handleTaskDialogClose = () => {
        setTaskDialogOpen(false);
        setSelectedTaskId(null);
        setTaskViewMode(false);
    };

    /*
     * Load tasks attached to this Change Request
     */
    const loadTasks = async () => {

        if (!change?.id) {
            return;
        }

        setLoading(true);

        try {

            const response = await AxiosInstance.get(
                `${CHANGE_REQUEST_URL}/${change.id}/tasks`
            );

            const data =
                response.data?.data ??
                response.data ??
                [];

            setTasks(
                Array.isArray(data)
                    ? data
                    : []
            );

        } catch (error) {

            console.error(
                "Unable to load change tasks",
                error
            );

            showAlert(
                "Unable to load tasks",
                "error"
            );

        } finally {

            setLoading(false);
        }
    };

    useEffect(() => {

        loadTasks();

    }, [change?.id]);

    const formatDate = (value) => {

        if (!value) {
            return "-";
        }

        return new Date(value).toLocaleDateString();
    };


    return (
        <Box>

            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2
                }}
            >

                <Box>
                    <Typography
                        variant="h6"
                        fontWeight={600}
                    >
                        Tasks
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Tasks associated with this change request
                    </Typography>
                </Box>


                <IconButton
                    color="primary"
                    onClick={handleAddTask}
                >
                    <AddIcon />
                </IconButton>

            </Box>


            {/* Task List */}
            <Paper variant="outlined">

                {loading ? (

                    <Box
                        sx={{
                            minHeight: 200,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                        }}
                    >
                        <CircularProgress />
                    </Box>

                ) : tasks.length === 0 ? (

                    <Box
                        sx={{
                            minHeight: 180,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                        }}
                    >

                        <Stack
                            spacing={1}
                            alignItems="center"
                        >

                            <Typography
                                variant="body1"
                                color="text.secondary"
                            >
                                No tasks added
                            </Typography>

                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={
                                    <AddOutlinedIcon />
                                }
                                onClick={() =>
                                    setTaskDialogOpen(true)
                                }
                            >
                                Add Task
                            </Button>

                        </Stack>

                    </Box>

                ) : (

                    <TableContainer>

                        <Table>

                            <TableHead>

                                <TableRow>

                                    <TableCell>
                                        Task ID
                                    </TableCell>

                                    <TableCell>
                                        Title
                                    </TableCell>

                                    <TableCell>
                                        Assignee
                                    </TableCell>

                                    <TableCell>
                                        Status
                                    </TableCell>

                                    <TableCell>
                                        Priority
                                    </TableCell>

                                    <TableCell>
                                        Due
                                    </TableCell>

                                    <TableCell>
                                        Stage
                                    </TableCell>
                                    <TableCell align="center">
                                        Action
                                    </TableCell>

                                </TableRow>

                            </TableHead>


                            <TableBody>

                                {tasks.map((task) => (

                                    <TableRow
                                        key={task.id}
                                        hover
                                    >

                                        <TableCell>

                                            <Tooltip title="View Task">

                                                <Link
                                                    size="small"
                                                    onClick={() =>
                                                        task.isCompleted ? handleViewTask(task.taskId) : handleUpdateTask(task.taskId)
                                                    }
                                                >
                                                    {task.taskId}
                                                </Link>


                                            </Tooltip>
                                        </TableCell>



                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                fontWeight={500}
                                            >
                                                {task.title}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            {task.assigneeName ||
                                                task.assignee ||
                                                "-"}
                                        </TableCell>

                                        <TableCell>
                                            {task.statusName ||
                                                task.status ||
                                                "-"}
                                        </TableCell>

                                        <TableCell>
                                            {task.priorityName ||
                                                task.priority ||
                                                "-"}
                                        </TableCell>

                                        <TableCell>
                                            {formatDate(task.due)}
                                        </TableCell>

                                        <TableCell>
                                            {task.changeStatusName ||
                                                "-"}
                                        </TableCell>

                                        <TableCell align="center">

                                            <Button
                                                variant={task.isCompleted ? "text" : "contained"}
                                                color={task.isCompleted ? "success" : "primary"}
                                                size="small"
                                                disabled={task.isCompleted} // Disables the button if already complete
                                                startIcon={task.isCompleted ? <CheckCircleOutlineIcon /> : null}
                                                onClick={() => onComplete(task.taskId)} // Your completion event handler

                                            >
                                                {task.isCompleted ? 'Completed' : 'Complete'}
                                            </Button>

                                        </TableCell>

                                    </TableRow>

                                ))}

                            </TableBody>

                        </Table>

                    </TableContainer>

                )}

            </Paper>


            {/* Add Task Dialog */}

            <ChangeTaskDialog
                open={taskDialogOpen}
                onClose={() =>
                    setTaskDialogOpen(false)
                }
                changeRequestId={change?.id}
                taskId={selectedTaskId}
                viewMode={taskViewMode}
                onSaved={handleTaskSaved}

            />

            <CloseTaskDialog
                open={closeTaskDialogOpen}
                onClose={() => {
                    setCloseTaskDialogOpen(false);
                    setSelectedTaskId(null);
                }}
                onSubmit={handleTaskComplete}
            />

        </Box>
    );
};

export default TasksTab;

